// components/admin/UserProfile.jsx
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  ChevronLeft, UserCheck, Calendar, Clock, Building, Mail,
  FileText, Database, Crown, Shield,
  Lock, Activity, LogIn, Upload, User, Key, CheckCircle, AlertCircle
} from 'lucide-react';
import { adminService } from '../../apis/adminService';

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function computeDaysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatBillingPeriod(period) {
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'monthly' || p === 'month') return 'Monthly';
  if (p === 'annually' || p === 'annual' || p === 'yearly' || p === 'year')
    return 'Annual';
  if (p === 'custom') return 'Custom';
  return String(period).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function mapPlanKeyToDisplay(planKey, { isFree, planTypeLower }) {
  const lower = String(planKey || '').toLowerCase();
  if (lower === 'free') return 'Free';
  if (lower === 'starter') return 'Starter';
  if (lower === 'professional' || lower === 'pro') return 'Professional';
  if (lower === 'individual') return 'Individual';
  if (lower === 'custom') return 'Custom';
  if (lower === 'group') return 'Group';
  if (lower === 'enterprise') return 'Enterprise';
  if (planKey) {
    return String(planKey)
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  if (isFree) return 'Free';
  if (planTypeLower === 'premium') return 'Professional';
  return null;
}

function planColorClass(plan) {
  const p = String(plan || '').toUpperCase();
  if (p === 'PROFESSIONAL' || p === 'PREMIUM')
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (p === 'STARTER') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (p === 'FREE') return 'bg-gray-100 text-gray-800 border-gray-200';
  if (p === 'INDIVIDUAL') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (p === 'CUSTOM' || p === 'GROUP' || p === 'CUSTOM / GROUP')
    return 'bg-purple-100 text-purple-800 border-purple-200';
  if (p === 'ENTERPRISE') return 'bg-indigo-100 text-indigo-800 border-indigo-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
}

function statusColorClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 'bg-teal-100 text-teal-800';
  if (s === 'trial' || s === 'trialing') return 'bg-blue-100 text-blue-800';
  if (s === 'expired') return 'bg-red-100 text-red-800';
  if (s === 'canceled' || s === 'cancelled')
    return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-800';
}

// Normalizes whatever shape the admin API returns into one consistent object.
function normalizeSubscription(user) {
  const sub = user?.subscription || {};
  const payment =
    sub.paymentInfo ||
    sub.latestPayment ||
    sub.payment ||
    user?.paymentInfo ||
    user?.latestPayment ||
    {};

  // ---- Plan identification ----
  const planKeyRaw =
    sub.planKey ||
    sub.plan_key ||
    payment.planKey ||
    payment.plan_key ||
    null;

  const planTypeLower = String(
    sub.plan_type || sub.planType || user?.planType || ''
  ).toLowerCase();
  const planKeyLower = String(planKeyRaw || '').toLowerCase();

  const isFree =
    planTypeLower === 'free' && (!planKeyRaw || planKeyLower === 'free');

  const statusLower = String(sub.status || '').toLowerCase();
  const isTrial = Boolean(
    sub.isTrial ??
      sub.is_trial ??
      (statusLower === 'trial' || statusLower === 'trialing')
  );

  const plan = mapPlanKeyToDisplay(planKeyRaw, { isFree, planTypeLower });
  const status = isTrial ? 'trial' : sub.status || 'active';

  // ---- Subscription window ----
  const subscriptionStart =
    sub.subscriptionStart ||
    sub.subscription_start ||
    payment.coverageStart ||
    payment.coverage_start ||
    null;

  const subscriptionEnd =
    sub.subscriptionEnd ||
    sub.subscription_end ||
    payment.coverageEnd ||
    payment.coverage_end ||
    null;

  // ---- Billing period ----
  const billingPeriodRaw =
    payment.billingPeriod ||
    payment.billing_period ||
    sub.billingPeriod ||
    sub.billing_period ||
    null;

  // ---- Amount / price ----
  const rawAmount = payment.amount ?? sub.amount ?? null;
  let amount = null;
  if (rawAmount != null) {
    const n =
      typeof rawAmount === 'string' ? parseFloat(rawAmount) : Number(rawAmount);
    if (!Number.isNaN(n)) amount = n;
  }
  const currency = payment.currency || sub.currency || 'USD';

  // ---- Payment status ----
  const paymentStatusRaw =
    payment.status ||
    sub.paymentStatus ||
    sub.payment_status ||
    null;
  const paymentStatus = paymentStatusRaw
    ? String(paymentStatusRaw).charAt(0).toUpperCase() +
      String(paymentStatusRaw).slice(1)
    : null;

  // ---- Seats ----
  const seatCount =
    sub.seatCount ??
    sub.seat_count ??
    payment.seatCount ??
    payment.seat_count ??
    null;

  // ---- Trial details ----
  const trialStarted =
    sub.trialStartedAt || sub.trial_started_at || null;
  const trialEnds =
    sub.trialEndsAt ||
    sub.trial_ends_at ||
    subscriptionEnd ||
    null;
  const trialDays =
    sub.trialDays ??
    sub.trial_days ??
    sub.accountLimits?.trialPeriod?.trialDays ??
    null;

  // ---- Days remaining (always computed from an end date) ----
  let daysRemaining = null;
  if (typeof sub.subscriptionDaysRemaining === 'number') {
    daysRemaining = sub.subscriptionDaysRemaining;
  } else if (typeof sub.daysRemaining === 'number' && sub.daysRemaining >= 0) {
    daysRemaining = sub.daysRemaining;
  } else {
    const target = isTrial ? trialEnds || subscriptionEnd : subscriptionEnd;
    daysRemaining = computeDaysRemaining(target);
  }

  return {
    plan,
    planKey: planKeyRaw,
    isFree,
    isTrial,
    status,
    billingPeriodRaw,
    billingPeriodLabel: formatBillingPeriod(billingPeriodRaw),
    subscriptionStart,
    subscriptionEnd,
    daysRemaining,
    seatCount,
    amount,
    currency,
    paymentStatus,
    trialStarted,
    trialEnds,
    trialDays,
  };
}

// ------------------------------------------------------------------
// Subcomponents
// ------------------------------------------------------------------

const ProfileHeader = ({ user }) => {
  const initials =
    user.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ||
    user.email?.[0]?.toUpperCase() ||
    '?';
  const statusColor =
    user.status === 'active'
      ? 'bg-teal-100 text-teal-800'
      : 'bg-gray-100 text-gray-800';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-100 to-teal-100 flex items-center justify-center flex-shrink-0">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-full w-full object-cover rounded-full"
            />
          ) : (
            <span className="text-3xl font-bold text-blue-700">{initials}</span>
          )}
        </div>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                {user.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span className="flex items-center text-gray-600 text-sm">
                  <Mail className="w-4 h-4 mr-1" />
                  {user.email}
                </span>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor}`}
                >
                  <UserCheck className="w-3 h-3 mr-1" />
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                {user.role === 'admin' ? 'Admin' : 'User'}
              </span>
              <span className="px-4 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
                {user.organization}
              </span>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center text-gray-500">
              <Calendar className="w-4 h-4 mr-2" />
              Joined{' '}
              {user.createdAt
                ? new Date(user.createdAt).toLocaleDateString()
                : 'N/A'}
            </div>
            <div className="flex items-center text-gray-500">
              <Clock className="w-4 h-4 mr-2" />
              Last login{' '}
              {user.lastLogin
                ? new Date(user.lastLogin).toLocaleString()
                : 'Never'}
            </div>
            <div className="flex items-center text-gray-500">
              <Building className="w-4 h-4 mr-2" />
              VPS ID: {user.vpsId || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UsageStats = ({ usage }) => {
  const stats = [
    {
      title: 'Total Sessions',
      value: usage.totalSessions || 0,
      icon: Clock,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      desc: 'All time sessions',
    },
    {
      title: 'SOAP Notes Generated',
      value: usage.soapNotesGenerated || 0,
      icon: FileText,
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      desc: 'Medical notes created',
    },
    {
      title: 'Token Usage',
      value: (usage.tokenUsage || 0).toLocaleString(),
      icon: Database,
      color: 'text-red-600',
      bg: 'bg-red-50',
      desc: 'Total tokens consumed',
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {stats.map((s, i) => (
        <div
          key={i}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">{s.title}</p>
              <p className="text-3xl font-bold text-gray-900">{s.value}</p>
            </div>
            <div
              className={`h-12 w-12 rounded-full ${s.bg} flex items-center justify-center`}
            >
              <s.icon className={`h-6 w-6 ${s.color}`} />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">{s.desc}</p>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------
// Subscription Card — now shows Days Remaining for every plan
// ------------------------------------------------------------------
const SubscriptionCard = ({ subscription }) => {
  if (!subscription) return null;

  const {
    plan,
    isFree,
    isTrial,
    status,
    billingPeriodRaw,
    billingPeriodLabel,
    subscriptionStart,
    subscriptionEnd,
    daysRemaining,
    seatCount,
    amount,
    paymentStatus,
    trialStarted,
    trialEnds,
    trialDays,
  } = subscription;

  const rows = [];

  // ---- Billing period (only if we have one) ----
  if (billingPeriodLabel) {
    rows.push({ label: 'Billing', value: billingPeriodLabel });
  }

  // ---- Price (only when amount present and > 0) ----
  if (amount != null && amount > 0) {
    const unit =
      String(billingPeriodRaw || '').toLowerCase().startsWith('annual')
        ? 'year'
        : 'month';
    const priceValue = `$${amount.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} / ${unit}`;
    rows.push({ label: 'Price', value: priceValue });
  }

  if (isTrial) {
    // Trial-specific rows
    if (trialDays) rows.push({ label: 'Trial', value: `${trialDays} days` });
    const started = formatDate(trialStarted);
    if (started) rows.push({ label: 'Trial Started', value: started });
    const ends = formatDate(trialEnds);
    if (ends) rows.push({ label: 'Trial Ends', value: ends });
  } else {
    // Paid users — show window + seats + payment
    if (!isFree) {
      const started = formatDate(subscriptionStart);
      if (started) rows.push({ label: 'Started', value: started });

      const renews = formatDate(subscriptionEnd);
      if (renews) rows.push({ label: 'Renews', value: renews });

      if (seatCount != null && seatCount > 0) {
        rows.push({ label: 'Seats', value: seatCount });
      }
      if (paymentStatus) {
        rows.push({ label: 'Payment', value: paymentStatus });
      }
    }
  }

  // ---- Days Remaining — shown for ALL plans (Free, Starter, Pro, etc.) ----
  if (typeof daysRemaining === 'number' && daysRemaining >= 0) {
    rows.push({ label: 'Days Remaining', value: daysRemaining });
  }

  const statusIcon =
    status === 'active' ? (
      <CheckCircle className="w-4 h-4 mr-1" />
    ) : (
      <AlertCircle className="w-4 h-4 mr-1" />
    );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Crown className="w-5 h-5 mr-2 text-yellow-500" />
        Subscription
      </h3>
      <div className="space-y-3 flex-1">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Plan</span>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium border ${planColorClass(
              plan
            )}`}
          >
            {plan || '—'}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Status</span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorClass(
              status
            )}`}
          >
            {statusIcon}
            {String(status).charAt(0).toUpperCase() + String(status).slice(1)}
          </span>
        </div>
        {rows.map((row, i) => (
          <div key={i} className="flex justify-between">
            <span className="text-sm text-gray-500">{row.label}</span>
            <span className="text-sm font-medium text-gray-900 text-right">
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Merged Security & Compliance Card
const SecurityComplianceCard = ({ security, compliance }) => {
  const items = [
    {
      label: 'Last Login Activity',
      value: security.lastLoginActivity
        ? new Date(security.lastLoginActivity).toLocaleString()
        : 'Never',
      icon: Clock,
      status: true,
    },
    {
      label: 'Account Verification',
      value: security.accountVerified ? 'Verified' : 'Unverified',
      icon: UserCheck,
      status: security.accountVerified,
    },
    {
      label: 'MFA Enabled',
      value: compliance.mfaEnabled ? 'Enabled' : 'Disabled',
      icon: Shield,
      status: compliance.mfaEnabled,
    },
    {
      label: 'HIPAA Agreement Accepted',
      value: compliance.hipaaAgreementAccepted ? 'Accepted' : 'Not Accepted',
      icon: CheckCircle,
      status: compliance.hipaaAgreementAccepted,
    },
    {
      label: 'Data Encryption Enabled',
      value: compliance.dataEncryptionEnabled ? 'Enabled' : 'Disabled',
      icon: Lock,
      status: compliance.dataEncryptionEnabled,
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Shield className="w-5 h-5 mr-2 text-teal-500" />
        Security &amp; Compliance
      </h3>
      <div className="space-y-3 flex-1">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-600">
              <item.icon className="w-4 h-4 mr-2 text-gray-400" />
              {item.label}
            </div>
            <div className="flex items-center">
              <span
                className={`text-sm font-medium ${
                  item.status ? 'text-teal-600' : 'text-red-600'
                }`}
              >
                {item.value}
              </span>
              <span
                className={`ml-2 w-2 h-2 rounded-full ${
                  item.status ? 'bg-teal-500' : 'bg-red-500'
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ActivityTimeline = ({ activities }) => {
  const getIcon = (type) =>
    ({
      login: LogIn,
      upload: Upload,
      soap: FileText,
      profile: User,
      password: Key,
    }[type] || Activity);

  const getColor = (type) =>
    ({
      login: 'text-blue-500 bg-blue-100',
      upload: 'text-blue-500 bg-blue-100',
      soap: 'text-teal-500 bg-teal-100',
      profile: 'text-yellow-500 bg-yellow-100',
      password: 'text-red-500 bg-red-100',
    }[type] || 'text-gray-500 bg-gray-100');

  const formatActivityDate = (d) =>
    d
      ? new Date(d).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : 'N/A';

  const hasActivities = activities && activities.length > 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-blue-500" />
        Recent Activity
      </h3>
      {!hasActivities ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 py-4">
          <div className="text-center">
            <Activity className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">No recent activity</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto max-h-96 pr-2 custom-scrollbar">
          <div className="flow-root">
            <ul className="-mb-8">
              {activities.slice(0, 20).map((act, idx) => {
                const Icon = getIcon(act.type);
                const colorClass = getColor(act.type);
                return (
                  <li key={idx}>
                    <div className="relative pb-8">
                      {idx < activities.length - 1 && (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span
                            className={`h-8 w-8 rounded-full ${colorClass} flex items-center justify-center ring-8 ring-white`}
                          >
                            <Icon className="w-4 h-4" />
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">
                            {act.description ||
                              act.type
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </p>
                          <p className="text-sm text-gray-500">
                            <Clock className="inline w-3 h-3 mr-1" />
                            {formatActivityDate(act.date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const UserProfileSkeleton = ({ onBack, topRef }) => (
  <div ref={topRef} className="min-h-screen bg-gray-50 p-4 sm:p-6 animate-pulse">
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center text-blue-600 mb-6">
        <ChevronLeft className="w-5 h-5 mr-2" />
        <span className="text-lg font-medium">Back to Users</span>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="h-24 w-24 rounded-full bg-gray-200" />
          <div className="flex-1 space-y-4">
            <div className="h-8 w-48 bg-gray-200 rounded" />
            <div className="h-5 w-64 bg-gray-200 rounded" />
            <div className="flex gap-4">
              <div className="h-10 w-24 bg-gray-200 rounded-full" />
              <div className="h-10 w-32 bg-gray-200 rounded-full" />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
            <div className="h-8 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-4 w-32 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-col lg:flex-row gap-6">
        <div className="lg:w-1/3 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-64" />
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-64" />
        </div>
        <div className="lg:w-2/3 bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-96" />
      </div>
    </div>
  </div>
);

// ------------------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------------------
const UserProfile = ({ user, onBack }) => {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const topRef = useRef(null);

  // The page scrolls inside a layout container (not window), so use
  // scrollIntoView which scrolls whichever ancestor is scrollable.
  useLayoutEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [isLoading]);
  
  useEffect(() => {
    if (user?.userId) {
      fetchUserData(user.userId);
    } else {
      setProfileData(transformUserData(user));
      setIsLoading(false);
    }
  }, [user]);

  const fetchUserData = async (userId) => {
    setIsLoading(true);
    setError(null);
    try {
      let filterParam = {};
      if (user.roleid === 4) {
        filterParam.assignedTo = userId;
      } else {
        filterParam.createdBy = userId;
      }

      const soapResponse = await adminService.getDashboardSOAPNotes({
        ...filterParam,
        limit: 50,
      });

      let totalSessions = 0;
      let soapNotesGenerated = 0;
      let activities = [];

      if (soapResponse.success && soapResponse.data?.soapNotes) {
        const notes = soapResponse.data.soapNotes;
        soapNotesGenerated =
          soapResponse.data.pagination?.total ?? notes.length;

        const sessionIds = new Set();
        notes.forEach((note) => {
          if (note.sessionId) sessionIds.add(note.sessionId);
          activities.push({
            type: 'soap',
            date: note.createdAt || note.lastModified,
            description: `SOAP note generated${
              note.patientName ? ` for ${note.patientName}` : ''
            }`,
          });
        });
        totalSessions = sessionIds.size;
      }

      try {
        const sessionsResponse = await adminService.getSessionsByUser(userId, {
          limit: 1000,
        });
        if (sessionsResponse.success && sessionsResponse.data?.sessions) {
          totalSessions = sessionsResponse.data.sessions.length;
        } else if (sessionsResponse.data?.total !== undefined) {
          totalSessions = sessionsResponse.data.total;
        }
      } catch (sessionsErr) {
        console.warn('Sessions API not available, using SOAP notes unique IDs.');
      }

      if (user.lastLogin) {
        activities.push({
          type: 'login',
          date: user.lastLogin,
          description: 'User logged in',
        });
      }

      activities.sort((a, b) => new Date(b.date) - new Date(a.date));

      const tokenUsage = user.subscription?.trial_usage_count || 0;
      const vpsId = user.profile?.id || 'N/A';

      const fullData = {
        user: {
          id: user.userId,
          name:
            user.profile?.firstName && user.profile?.lastName
              ? `${user.profile.firstName} ${user.profile.lastName}`
              : user.email || 'Unknown User',
          email: user.email,
          role: user.role?.role || (user.roleid === 2 ? 'admin' : 'user'),
          status: user.isActive ? 'active' : 'inactive',
          organization: user.profile?.organization || user.clinicName || 'N/A',
          createdAt: user.createdAt,
          lastLogin: user.lastLogin,
          avatar: user.profile?.profileimage || null,
          vpsId,
        },
        usage: {
          totalSessions,
          soapNotesGenerated,
          tokenUsage,
        },
        subscription: normalizeSubscription(user),
        security: {
          lastLoginActivity: user.lastLogin,
          accountVerified: user.emailVerified ?? true,
        },
        compliance: {
          mfaEnabled: user.profile?.mfaEnabled || false,
          hipaaAgreementAccepted: true,
          auditLoggingEnabled: true,
          accountVerified: user.emailVerified ?? true,
          dataEncryptionEnabled: true,
        },
        activity: activities,
      };

      setProfileData(fullData);
    } catch (err) {
      console.error('Error fetching user profile data:', err);
      setError('Unable to load full profile. Showing basic data.');
      setProfileData(transformUserData(user));
    } finally {
      setIsLoading(false);
    }
  };

  const transformUserData = (user) => {
    const fullName =
      user.profile?.firstName || user.profile?.lastName
        ? `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim()
        : user.email || 'Unknown User';
    const vpsId = user.profile?.id || 'N/A';

    return {
      user: {
        id: user.userId,
        name: fullName,
        email: user.email,
        role: user.role?.role || (user.roleid === 2 ? 'admin' : 'user'),
        status: user.isActive ? 'active' : 'inactive',
        organization: user.profile?.organization || user.clinicName || 'N/A',
        createdAt: user.createdAt,
        lastLogin: user.lastLogin,
        avatar: user.profile?.profileimage || null,
        vpsId,
      },
      usage: {
        totalSessions: 0,
        soapNotesGenerated: 0,
        tokenUsage: user.subscription?.trial_usage_count || 0,
      },
      subscription: normalizeSubscription(user),
      security: { lastLoginActivity: user.lastLogin, accountVerified: true },
      compliance: {
        mfaEnabled: false,
        hipaaAgreementAccepted: true,
        auditLoggingEnabled: true,
        accountVerified: true,
        dataEncryptionEnabled: true,
      },
      activity: [
        { type: 'login', date: user.lastLogin, description: 'Last login' },
      ],
    };
  };

  if (isLoading) return <UserProfileSkeleton onBack={onBack} topRef={topRef} />;
  if (error && !profileData)
    return (
      <div ref={topRef} className="min-h-screen bg-gray-50 p-4 sm:p-6 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-md">
          <p className="text-red-600 text-lg font-medium">{error}</p>
          <button
            onClick={onBack}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Go back
          </button>
        </div>
      </div>
    );

  const {
    user: userInfo,
    usage,
    subscription,
    security,
    compliance,
    activity,
  } = profileData;

  return (
    <div ref={topRef} className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-800 group mb-6"
        >
          <ChevronLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="text-lg font-medium">Back to Users</span>
        </button>
        <ProfileHeader user={userInfo} />
        <div className="mt-8">
          <UsageStats usage={usage} />
        </div>
        {/* Two-column section with equal height */}
        <div className="mt-8 flex flex-col lg:flex-row gap-6">
          <div className="lg:w-1/3 flex flex-col gap-6">
            <SubscriptionCard subscription={subscription} />
            <SecurityComplianceCard security={security} compliance={compliance} />
          </div>
          <div className="lg:w-2/3">
            <ActivityTimeline activities={activity} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;